
import gsap from 'gsap'
import { getOperatingSystem } from '../utils/get-operating-system'
import { throttle } from 'throttle-debounce'

const KonfektPage2 = {
  init(): void {
    if (!document.querySelector('.konfekt-page.konfekt-page--2')) {
      return
    }

    const start = () => {    
      this.handleIntroAnimations()
      this.handleForm()
      this.handleCardHovers()
      this.handleParallaxes()
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        start()
      }, { once: true })
    } else {
      start()
    }
  },

  handleIntroAnimations(): void {
    // Intro animation for hero section

    if (document.querySelector('.kp-hero')) {
      gsap.to('.kp-rays svg', { rotation: 360, duration: 123, ease: 'none', repeat: -1 })
      gsap.set('.kp-loader', { display: 'none' })
  
      gsap
        .timeline()
        .to('.kp-cover > div', { duration: 0.7, ease: 'power2.inOut', x: 0 }, 0.2)
        .set('.kp-container', { visibility: 'visible' })
        .to('.kp-cover > div', { duration: 0.7, ease: 'power2.inOut', xPercent: 100 })
        .set('.kp-cover', { display: 'none' })

      // Reveal hero title, supertitle, text & button

      const heroRevealStart = 0.44

      gsap
        .timeline()
        .set('.kp-header', { filter: 'blur(8rem)', opacity: 0 })
        .set('.kp-hero__title .kp-title__mask', { xPercent: -100 })
        .set('.kp-hero__supertitle, .kp-hero .kp-title__small-text, .kp-hero__form-container', { filter: 'blur(8rem)', opacity: 0 })
        .set('.kp-candy--hero', { yPercent: -10 })
        .to('.kp-hero__title .kp-title__overlay', { duration: 2, ease: 'power2.inOut', xPercent: 100 }, heroRevealStart)
        .to('.kp-hero__title .kp-title__mask', { duration: 4, ease: 'power2.out', xPercent: 50 }, '>-1.4')
        .to('.kp-hero__supertitle', { duration: 2, filter: 'blur(0rem)', opacity: 1 }, '<+0.4')
        .to('.kp-header', { duration: 2, filter: 'blur(0rem)', opacity: 1 }, '<+0.2')
        .to('.kp-candy--hero', { duration: 3, ease: 'power2.out', yPercent: 0 }, heroRevealStart)
        .to('.kp-hero .kp-title__small-text, .kp-hero__form-container', { duration: 1, filter: 'blur(0rem)', opacity: 1, stagger: 0.6 }, '>-1.7')
    }
    
    // Intro animation for loader & info section

    if (document.querySelector('.kp-info')) {
      gsap
        .timeline()
        .set('.kp-loader', { opacity: 0 })
        .set('.kp-info', { display: 'none' })
        .set('.kp-cover', { display: 'none' })
        .set('.kp-container', { visibility: 'visible' })
        .to('.kp-loader', { duration: 0.5, ease: 'power2.out', opacity: 1 })
        .to('.kp-loader', { duration: 0.5, ease: 'power2.out', opacity: 0 }, 1.5)
        .set('.kp-loader', { display: 'none' })
        .set('.kp-info', { clearProps: 'display', opacity: 0, scale: 0.9 })
        .to('.kp-info', { clearProps: 'all', duration: 1.23, ease: 'power3.inOut', opacity: 1, scale: 1 })
    }
  },

  handleForm(): void {
    if (!document.querySelector('.kp-hero--win')) {
      return
    }

    // Hide the form on initial load

    gsap
      .timeline()
      .set('.kp-hero__form', { pointerEvents: 'none' })
      .set('.kp-hero__form-inputs', { height: 0 })
      .set('.kp-hero__form .kp-input', { opacity: 0 })
      .set('.kp-hero__form .kp-button--submit', { display: 'none' })

    // Handle form reveal on button click

    document.querySelector<HTMLElement>('.kp-hero .kp-button--register').addEventListener('click', () => {
      gsap.matchMedia().add({
        isDesktop: `(min-width: 768px)`,
        isMobile: `(max-width: 767px)`,
      }, (context) => {
        const { isMobile } = context.conditions

        gsap
          .timeline()
          .set('.kp-hero__form .kp-button--submit', { clearProps: 'display', marginTop: -document.querySelector<HTMLElement>('.kp-hero .kp-button--register').offsetHeight, opacity: 0 })
          .to('.kp-hero .kp-button--register', { duration: 0.5, ease: 'power2.out', opacity: 0, pointerEvents: 'none' })
          .to('.kp-hero .kp-button--submit', { duration: 0.5, ease: 'power2.out', opacity: 1 }, '<')
          .set('.kp-hero .kp-button--register', { display: 'none' })
          .set('.kp-hero__form', { clearProps: 'all' })
          .set('.kp-hero__form .kp-button--submit', { clearProps: 'margin' }, '<')
          .to('.kp-hero__title-container-2', { duration: 1, ease: 'power3.inOut', marginTop: '-60rem', marginBottom: isMobile ? '50rem' : 0, scale: 0.8 })
          .to('.kp-hero__form-inputs', { duration: 1, ease: 'power3.inOut', height: 'auto' }, '<')
          .to('.kp-hero__form .kp-input', { duration: 0.6, opacity: 1, stagger: 0.2 }, '<0.1')
      })
    })

    // Handle form submit

    document.querySelector<HTMLFormElement>('.kp-hero__form').addEventListener('submit', (e: Event) => {
      e.preventDefault()

      gsap
        .timeline()
        .to('.kp-hero .kp-candy', { duration: 1.23, ease: 'power3.inOut', scale: 1.2 })
        .to('.kp-hero', { duration: 1.23, ease: 'power3.inOut', filter: 'blur(8rem)', opacity: 0, scale: 0.9, onComplete: () => {
          gsap.set('.kp-hero', { display: 'none' })
          gsap.set('.kp-loader', { clearProps: 'display' })
          setTimeout(() => {
            document.querySelector<HTMLFormElement>('.kp-hero__form').submit()
          }, 543)
        }}, '<')
    })
  },

  handleCardHovers(): void {
    if (getOperatingSystem() !== 'desktop' || !document.querySelector('.kp-info')) {
      return
    }

    // Moving product cards

    const commonProps = {
      duration: 1.23,
      ease: 'power3.out',
      overwrite: true,
    }

    // Moving info & details card

    document.body.addEventListener('mousemove', throttle(60, (e: MouseEvent) => {
      const x = e.clientX
      const y = e.clientY
      const xPercent = ((x / window.innerWidth) * 100) - 50
      const yPercent = ((y / window.innerHeight) * 100) - 50
      const rotationModifier = 0.021

      gsap.to('.kp-info__inner-2', {
        ...commonProps,
        rotateX: -yPercent * rotationModifier,
        rotateY: xPercent * rotationModifier,
      })

      gsap.to('.kp-info__picture img', {
        ...commonProps,
        duration: 2.1,
        x: `${xPercent / 3.2}rem`,
      })
    }))
  },

  handleParallaxes(): void {
    const candies = document.querySelectorAll<HTMLElement>('.kp-candy > div')

    if (candies.length === 0) {
      return
    }
    
    // Handle mousemove

    document.body.addEventListener('mousemove', throttle(60, (e: MouseEvent) => {
      if (getOperatingSystem() !== 'desktop') {
        return
      }

      const x = e.clientX
      const y = e.clientY
      const xPercent = ((x / window.innerWidth) * 100) - 50
      const yPercent = ((y / window.innerHeight) * 100) - 50

      candies.forEach((candy) => {
        const transformModifier = gsap.utils.random(2.5, 3.5, 0.1)

        gsap.to(candy.querySelector('picture'), {
          duration: 3.45,
          ease: 'power3.out',
          overwrite: true,
          rotation: -xPercent / 10,
          x: `${-xPercent / transformModifier}rem`,
          y: `${-yPercent / transformModifier}rem`,
        })
      })
    }))
  },
}

export default KonfektPage2
